import { Suspense, useEffect } from 'react';
import { Navigate, Outlet, useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';

import { AppShell } from '@/app/layout/app-shell';
import { LoadingScreen } from '@/components/feedback/loading-screen';
import { useSessionStore } from '@/state/session-store';

function DesktopAuthCallbackBridge() {
  const navigate = useNavigate();

  useEffect(() => {
    let disposed = false;
    const desktopAuth = window.desktop?.auth;

    if (!desktopAuth) {
      return () => {
        disposed = true;
      };
    }

    const handleCallback = (inviteCallbackUrl: string) => {
      navigate('/auth/accept-invite', {
        replace: true,
        state: {
          inviteCallbackUrl,
        },
      });
    };

    void desktopAuth.consumePendingInviteCallback().then((inviteCallbackUrl) => {
      if (!disposed && inviteCallbackUrl) {
        handleCallback(inviteCallbackUrl);
      }
    });

    const unsubscribe = desktopAuth.onInviteCallback((inviteCallbackUrl) => {
      if (!disposed) {
        handleCallback(inviteCallbackUrl);
      }
    });

    return () => {
      disposed = true;
      unsubscribe();
    };
  }, [navigate]);

  return null;
}

export function RouterSuspense() {
  return (
    <>
      <DesktopAuthCallbackBridge />
      <Suspense fallback={<LoadingScreen message="Preparing workspace..." />}>
        <Outlet />
      </Suspense>
    </>
  );
}

export function RootRedirect() {
  const { bootstrapped, user } = useSessionStore(
    useShallow((state) => ({
      bootstrapped: state.bootstrapped,
      user: state.user,
    })),
  );

  if (!bootstrapped) {
    return <LoadingScreen />;
  }

  return <Navigate to={user ? '/dashboard' : '/auth'} replace />;
}

export function ProtectedLayout() {
  const { bootstrapped, user } = useSessionStore(
    useShallow((state) => ({
      bootstrapped: state.bootstrapped,
      user: state.user,
    })),
  );

  if (!bootstrapped) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return <AppShell />;
}
